import React, { useEffect, useState } from "react";
import {
  FileText,
  Settings,
  Star,
  Camera,
  User,
  Edit3,
  Save,
  Plus,
  X,
  Zap,
  Award,
  Image,
  Users,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Upload,
  Trash2,
} from "lucide-react";
import "./dashboard.css";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboard,
  updateQuickInfoText,
  uploadQuickInfoImages,
  deleteQuickInfoImage,
} from "../../../features/merchant/dashboardThunks";

import axiosInstance from "../../../api/axiosInstance";
import { toast } from "react-toastify";

const QuickInfoPage = () => {
  const dispatch = useDispatch();

  const { data: dashboardData, loading, updating } = useSelector(
    (state) => state.dashboard
  );

  const [isEditing, setIsEditing] = useState(false);
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState("");

  const [formData, setFormData] = useState({
    clientDetails: "",
    specialty: "",
    coverImages: [],
    profileImage: null,
  });

  useEffect(() => {
    dispatch(fetchDashboard());
  }, [dispatch]);

  useEffect(() => {
    if (!dashboardData) return;

    const qi = dashboardData.quickInfo || {};

    setFormData({
      clientDetails: qi.detailsOfClientsServed || "",
      specialty: qi.speciality || "",
      coverImages: qi.quickInfoCoverImages || [],
      profileImage: qi.visibleProfileImage || null,
    });

    setServices(qi.services || []);
  }, [dashboardData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddService = () => {
    if (newService.trim() && services.length < 5) {
      setServices([...services, newService.trim()]);
      setNewService("");
    }
  };

  const handleRemoveService = (index) => {
    if (!isEditing) return;
    setServices(services.filter((_, i) => i !== index));
  };

  const handleSaveText = async () => {
    try {
      await dispatch(
        updateQuickInfoText({
          detailsOfClientsServed: formData.clientDetails,
          services,
          speciality: formData.specialty,
        })
      ).unwrap();

      toast.success("Quick Info updated");
      setIsEditing(false);

      dispatch(fetchDashboard());
    } catch (err) {
      toast.error(err || "Failed updating quick info");
    }
  };

  const handleCoverImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const form = new FormData();
    files.forEach((file) => {
      form.append("quickInfoCoverImages", file);
    });

    try {
      await dispatch(uploadQuickInfoImages(form)).unwrap();
      toast.success("Images Uploaded");

      dispatch(fetchDashboard());
    } catch (err) {
      toast.error(err);
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("visibleProfileImage", file);

    try {
      await dispatch(uploadQuickInfoImages(form)).unwrap();
      toast.success("Profile Image Updated");

      dispatch(fetchDashboard());
    } catch (err) {
      toast.error(err);
    }
  };

  const removeImage = async (imagePath) => {
    try {
      const id = imagePath.split("/").pop();

      await dispatch(deleteQuickInfoImage(id)).unwrap();

      toast.success("Image deleted");
      dispatch(fetchDashboard());
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <div className="enhanced-dashboard">
      {/* Page Header */}
      <div className="page-header-banner quickinfo-header">
        <div className="header-content-wrapper">
          <div className="header-icon-box">
            <Zap size={28} />
          </div>
          <div className="header-text-content">
            <h1 className="header-main-heading">Quick Info</h1>
            <p className="header-sub-text">Highlight your services, speciality, and showcase images</p>
          </div>
        </div>
        <div className={`status-indicator ${isEditing ? 'editing' : 'viewing'}`}>
          {isEditing ? (
            <>
              <AlertCircle size={16} />
              <span>Editing Mode</span>
            </>
          ) : (
            <>
              <CheckCircle size={16} />
              <span>View Mode</span>
            </>
          )}
        </div>
      </div>

      <div className="dashboard-content-area">
        {/* Client Details Section */}
        <div className="enhanced-section-card">
          <div className="section-header-row">
            <div className="section-icon-title">
              <div className="section-icon gradient-purple">
                <Users size={22} />
              </div>
              <div>
                <h2 className="section-main-title">About</h2>
                <p className="section-subtitle">Details about your business </p>
              </div>
            </div>
          </div>

          <div className="form-group-enhanced">
            <label className="form-label-enhanced">
              <FileText size={16} />
              Details of Clients Served
            </label>
            <div className="input-with-icon">
              <FileText size={18} className="input-icon-left" />
              <input
                type="text"
                className="form-input-enhanced with-icon"
                placeholder="e.g., Served 500+ happy customers across industries"
                value={formData.clientDetails}
                onChange={(e) => handleInputChange("clientDetails", e.target.value)}
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>

        {/* Services & Specialty Grid */}
        <div className="two-column-grid">
          {/* Services Section */}
          <div className="enhanced-section-card">
            <div className="section-header-row">
              <div className="section-icon-title">
                <div className="section-icon gradient-blue">
                  <Settings size={22} />
                </div>
                <div>
                  <h2 className="section-main-title">Services Provided by Merchant</h2>
                  <p className="section-subtitle">What you offer (Max 5)</p>
                </div>
              </div>
            </div>

            <div className="services-tags-container">
              {services.length === 0 ? (
                <div className="empty-services">
                  <Settings size={24} />
                  <span>No services added yet</span>
                </div>
              ) : (
                services.map((service, idx) => (
                  <div key={idx} className="service-tag">
                    <Sparkles size={14} />
                    <span>{service}</span>
                    {isEditing && (
                      <button
                        className="service-tag-remove"
                        onClick={() => handleRemoveService(idx)}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {isEditing && services.length < 5 && (
              <div className="add-service-section">
                <div className="add-service-input-wrapper">
                  <input
                    type="text"
                    className="add-service-input"
                    placeholder="Type a service..."
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddService()}
                  />
                  <button
                    className="add-service-button"
                    onClick={handleAddService}
                    disabled={!newService.trim()}
                  >
                    <Plus size={18} />
                    Add
                  </button>
                </div>
                <span className="services-counter">{5 - services.length} slots remaining</span>
              </div>
            )}
          </div>

          {/* Specialty Section */}
          <div className="enhanced-section-card">
            <div className="section-header-row">
              <div className="section-icon-title">
                <div className="section-icon gradient-gold">
                  <Award size={22} />
                </div>
                <div>
                  <h2 className="section-main-title">Speciality (Only One)</h2>
                  <p className="section-subtitle">Your unique expertise</p>
                </div>
              </div>
            </div>

            <div className="specialty-display">
              {formData.specialty ? (
                <div className="specialty-badge">
                  <Star size={20} />
                  <span>{formData.specialty}</span>
                </div>
              ) : (
                <div className="empty-specialty">
                  <Award size={24} />
                  <span>No speciality set</span>
                </div>
              )}
            </div>

            {isEditing && (
              <div className="form-group-enhanced" style={{ marginTop: '1rem' }}>
                <div className="input-with-icon">
                  <Star size={18} className="input-icon-left" />
                  <input
                    type="text"
                    className="form-input-enhanced with-icon"
                    placeholder="Enter your speciality..."
                    value={formData.specialty}
                    onChange={(e) => handleInputChange("specialty", e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cover Images Section */}
        <div className="enhanced-section-card">
          <div className="section-header-row">
            <div className="section-icon-title">
              <div className="section-icon gradient-pink">
                <Image size={22} />
              </div>
              <div>
                <h2 className="section-main-title">Quick Info Cover Images</h2>
                <p className="section-subtitle">Showcase your work (Max 5 images)</p>
              </div>
            </div>
          </div>

          <div className="cover-images-grid">
            {Array.isArray(formData.coverImages) &&
              formData.coverImages.map((img, index) => {
                const src = `${axiosInstance.defaults.baseURL?.replace(/\/$/, "")}${img}`;
                return (
                  <div key={index} className="cover-image-card">
                    <img src={src} alt={`Cover ${index + 1}`} />
                    {isEditing && (
                      <button
                        className="cover-image-remove"
                        onClick={() => removeImage(img)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                );
              })}

            {isEditing && formData.coverImages?.length < 5 && (
              <label className="cover-image-upload-card">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden-file-input"
                  onChange={handleCoverImageUpload}
                />
                <div className="upload-content">
                  <div className="upload-icon-wrapper">
                    <Upload size={24} />
                  </div>
                  <span className="upload-text">Upload Images</span>
                  <span className="upload-hint">{5 - (formData.coverImages?.length || 0)} slots left</span>
                </div>
              </label>
            )}
          </div>

          {!isEditing && formData.coverImages?.length === 0 && (
            <div className="empty-images-state">
              <Camera size={32} />
              <span>No cover images uploaded</span>
            </div>
          )}
        </div>

        {/* Profile Image Section */}
        <div className="enhanced-section-card">
          <div className="section-header-row">
            <div className="section-icon-title">
              <div className="section-icon gradient-green">
                <User size={22} />
              </div>
              <div>
                <h2 className="section-main-title">Visible Profile Image</h2>
                <p className="section-subtitle">Your public visible profile picture</p>
              </div>
            </div>
          </div>

          <div className="profile-image-container">
            <div className="profile-image-wrapper">
              {formData.profileImage ? (
                <img
                  src={`${axiosInstance.defaults.baseURL?.replace(/\/$/, "")}${formData.profileImage}`}
                  alt="Profile"
                  className="profile-image-display"
                />
              ) : (
                <div className="profile-image-placeholder">
                  <User size={48} />
                </div>
              )}

              {isEditing && (
                <label className="profile-image-overlay">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden-file-input"
                    onChange={handleProfileImageUpload}
                  />
                  <Camera size={24} />
                  <span>Change Photo</span>
                </label>
              )}
            </div>

            <div className="profile-image-info">
              <h4>Profile Picture</h4>
              <p>This image will be visible to customers on your profile</p>
              {isEditing && (
                <span className="image-hint">Recommended: 400x400px, JPG or PNG</span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-row">
          {isEditing ? (
            <>
              <button
                className="action-btn-enhanced cancel-btn"
                onClick={() => {
                  setIsEditing(false);
                  dispatch(fetchDashboard());
                }}
              >
                <X size={20} />
                Cancel
              </button>
              <button
                className="action-btn-enhanced save-btn"
                onClick={handleSaveText}
                disabled={updating}
              >
                {updating ? (
                  <>
                    <span className="btn-loader"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              className="action-btn-enhanced edit-btn-enhanced"
              onClick={() => setIsEditing(true)}
              disabled={loading}
            >
              <Edit3 size={20} />
              Edit Quick Info
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickInfoPage;
